/**
 * =============================================================================
 * mp.js 
 * =============================================================================
 *
 * Entrypoint for all custom JS for this project. Any JS functionality should be
 * wrapped into Extension and placed in /scripts/vendor named
 * mp-extension-name.js.
 *
 * MP is our global object, attached to the window, with all utilities and
 * extensions
 * =============================================================================
 */

import Logger from "./utilities/logger";

var MP = {
  PreInitCallbacks: [],
  PostInitCallbacks: [],

  Extensions: {}, 

  Globals: {
    templates: {
        notification: null
    }
  },

  // Simple cache for API requests
  Cache: new Map(),

  HelperRegistry: new Map(),
  Helpers: {},

  Logger: new Logger(),

  Utilities: {
    sequence: "\x1b[30;45;1m",
    
    getProductJson: async function(ids = []) {
      ids = Array.isArray(ids) ? ids : [ids];
      let path = `/search?q=${ids.map(id => `id:${parseInt(id)}`).join('+OR+')}&view=json`;
      if(MP.Cache.has(path)){
        console.log(`${this.sequence}[MP]`, `Cache hit for`, path);
        return MP.Cache.get(path);
      }
      console.log(`${this.sequence}[MP]`, `Cache miss for`, path);
      let response = await fetch(path, this.fetchConfig());
      let json = await response.json();
      MP.Cache.set(path, json.results);
      return json.results;
    },

    /**
     * Shopify-style money formatter.
     * `cents` can be a string or number.
     */
    formatMoney: function(cents, format) {
      if (typeof cents == "string") {
        cents = cents.replace(".", "");
      }
      var value = "";
      var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      var formatString = format;

      function defaultOption(opt, def) {
        return typeof opt == "undefined" ? def : opt;
      }

      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultOption(precision, 2);
        thousands = defaultOption(thousands, ",");
        decimal = defaultOption(decimal, ".");

        if (isNaN(number) || number == null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        var parts = number.split("."),
          dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
          cents = parts[1] ? decimal + parts[1] : "";

        return dollars + cents;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    },


    isProductPage: function(){ 
      return $('.template-product').length > 0;
    }, 

    isCollectionPage: function(){
      return $('.template-collection').length > 0; 
    },

    isCartPage: function(){
      return $('.template-cart').length > 0; 
    },

    fetchConfig: function(type = 'json') {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
      };
    },

    getProductId: function() {
      if(MP.Utilities.isProductPage()) {
        return $("[data-product-id]").attr("data-product-id");
      } else {
        return null;
      }
    },

    getVariantId: function() {
      if(MP.Utilities.isProductPage()) {
        return $("form.product-form input[name='id']").val();
      } else {
        return null;
      }
    }, 

    /**
     * Show a Bootstrap Alert
     * 
     * @param {String} type 
     * @param {String} content 
     * @param {String} icon 
     */
    alert: function(type = "primary", content = "<strong>This</strong> is an alert", icon = "", closeAfter = 5000) {
      let alert = document.querySelector("#alert-container > .alert");
      alert.className = `${alert.className.replace(/\balert-.+/g, '')} alert-${type}`;
      let alertContent = alert.querySelector(".alert-message");
      content = `<div>${content}</div>`;
      if(icon){
        content = `<i class="fs-4 me-2 ${icon}"></i>${content}`;
      }
      alertContent.innerHTML = content;
      alert.classList.add("show");
      setTimeout(() => {
        alert.classList.remove("show")
      }, closeAfter);
    },

    /**
     * Show a Bootstrap Toast
     * 
     * @param {String} type 
     * @param {String} content 
     * @param {String} icon 
     */
    toast: function(type = "success", content = "<strong>This</strong> is a toast!", icon = "") {
      // Get the template and clone it
      let container = document.querySelector("#toasts");
      let template = document.querySelector('#toast');
      let clone = template.content.firstElementChild.cloneNode(true);

      // Set the alert class, message and optional icon
      let wrapper = clone.querySelector("div:first-child");
      wrapper.className = `${wrapper.className} alert-${type}`;
      clone.querySelector(".toast-message").innerHTML = content;
      if(icon){
        clone.querySelector(".toast-icon").innerHTML = `<i class="fs-4 me-2 ${icon}"></i>`;
      }

      // Add to the DOM, and show
      container.appendChild(clone);
      let toast = new bootstrap.Toast(clone);
      toast.show();
    }
  },
  
  registerHelper: function(name, callback, context = this) {
    if(this.HelperRegistry.has(name)){
      throw new Error(`Helper with name ${name} already exists`);
    }
    // Automatically bind the function to its original context
    const boundFn = callback.bind(context);

    this.HelperRegistry.set(name, (...args) => boundFn(...args));
    this.Helpers[name] = (...args) => boundFn(...args); // Quick access to helper function
  },

  init: function() {
    this.hasLoaded = true;
    let event = new CustomEvent("theme:loaded", {});
    document.dispatchEvent(event);

    var alertList = document.querySelectorAll('.alert')
    alertList.forEach(function (alert) {
      var alert = new bootstrap.Alert(alert);
    })

    document.addEventListener('full-cart:updated', mp_lazyload_setup); 

    // Run all pre-init callbacks registered before script was loaded 
    this.runPreInitCallbacks();

    // Call onReady method of all extensions once jQuery document ready fired
    $(function() { 
      MP.onReady();
      for (const [key, value] of Object.entries(MP.Extensions)) {
        if("onReady" in value){
          try {
            value.onReady(); 
          } catch (e) {
            console.error(e);
          }
        }
      }
    });

    // Call init method of all extensions straightaway
    for (const [key, value] of Object.entries(MP.Extensions)) {
      if("init" in value){
        try {
          value.init(); 
        } catch (e) {
          console.error(e);
        }
      }
    }

    // Run all post-init callbacks registered before script was loaded 
    this.runPostInitCallbacks();

    // Make all links with # in #MainContent to scroll to the href ID
    document.querySelectorAll("#MainContent a[href*='#']:not([data-hash-ignore])").forEach(element => {
      element.addEventListener("click", e => {
        document.querySelector(element.href).scrollIntoView({
          behavior: 'smooth'
        });
      });
    });

    // Remove elements with data attribute 'dismiss' when close icon clicked,
    // and set a cookie to prevent it from showing again for 7 days
    document.querySelectorAll("[data-dismiss]").forEach(element => {
      if(!Cookies.get(`dismiss-${element.dataset.dismiss}`) || Shopify.designMode){
        document.querySelector(element.dataset.dismiss)?.classList.remove("d-none");
      }
      element.addEventListener("click", e => {
        document.querySelector(element.dataset.dismiss)?.remove();
        Cookies.set(`dismiss-${element.dataset.dismiss}`, true, { expires: 7 });
      });
    });

    // Unhide elements with data attribute 'dismiss' when section loaded in
    // theme customiser.
    if(Shopify.designMode){
      document.addEventListener("shopify:section:load", e => {
        e.target.querySelectorAll("[data-dismiss]").forEach(element => {
          document.querySelector(element.dataset.dismiss)?.classList.remove("d-none");
          console.log("[MP] Unhide element with data-dismiss", element.dataset.dismiss);
        });
      });
    }

    // Enable all tooltips
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
    const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl))
  },

  runPreInitCallbacks: function() {
    // Run all pre-init callbacks registered before script was loaded 
    while (this.PreInitCallbacks.length){
      try {
        this.PreInitCallbacks.shift().call();
      } catch (e) {
        console.error("[MP] Failed to run callback with error", e);
      }
    }
  },

  runPostInitCallbacks: function() {
    // Run all post-init callbacks registered before script was loaded 
    while (this.PostInitCallbacks.length){
      try {
        this.PostInitCallbacks.shift().call();
      } catch (e) {
        console.error("[MP] Failed to run post-init callback with error", e);
      }
    }
  },

  onReady: function() {

  }
}

export default MP;